import React from 'react';

import Grid from '@mui/material/Grid';
import { Toolbar, DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';
import {
  Box,
  Card,
  Stack,
  Divider,
  Skeleton,
  Typography,
  CardContent,
} from '@mui/material';

import { Iconify } from '../../../../components/iconify';
import { MESSAGES } from '../../../../constants/messages';
import { InfoBox } from '../../shared/components/info-box';
import {
  getPamContact,
  sortCarrierContacts,
  type CarrierContactRecord,
  normalizeCarrierContactRecords,
} from './carrier-profile-contacts';
import {
  CustomToolbarQuickFilter,
  CustomToolbarExportButton,
  CustomToolbarFilterButton,
} from '../../../../components/custom-data-grid/toolbar-core';

import type { CarrierViewModel } from '../../../../api/types/CarrierViewModel';
import type { CarrierContactViewModel } from '../../../../api/types/CarrierContactViewModel';

export interface PolicyCarrierProfileProps {
  carrier?: CarrierViewModel | null;
  carrierContacts?: CarrierContactViewModel[] | null;
  isLoading?: boolean;
}

const groupingColumnDefinition = {
  headerName: MESSAGES.POLICY.CARRIER_PROFILE.ROLE_LABEL,
  minWidth: 220,
  flex: 1,
  // Make the grouped Role column the single filter target so the filter panel
  // starts editable and does not duplicate Role options.
  filterable: true,
};

const contactColumns: GridColDef<CarrierContactRecord>[] = [
  {
    field: 'role',
    headerName: MESSAGES.POLICY.CARRIER_PROFILE.ROLE_LABEL,
    minWidth: 220,
    flex: 1,
    // Hide the raw source role column from filter choices to avoid duplicate Role entries.
    filterable: false,
  },
  {
    field: 'contactName',
    headerName: MESSAGES.POLICY.CARRIER_PROFILE.CONTACT_NAME_LABEL,
    minWidth: 220,
    flex: 1.2,
  },
  {
    field: 'email',
    headerName: MESSAGES.POLICY.CARRIER_PROFILE.EMAIL_LABEL,
    minWidth: 260,
    flex: 1.2,
  },
  {
    field: 'phone',
    headerName: MESSAGES.POLICY.CARRIER_PROFILE.PHONE_LABEL,
    minWidth: 200,
    flex: 1,
  },
];

const tableRowHeight = 52;
const tableHeaderHeight = 56;

function CarrierContactsToolbar() {
  return (
    <Toolbar>
      <Box
        sx={{
          p: 2,
          gap: 2,
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <CustomToolbarQuickFilter
          debounceMs={300}
          sx={{
            flexGrow: 1,
            maxWidth: { xs: '100%', md: 420 },
            minWidth: { md: 340 },
          }}
          slotProps={{
            textField: {
              sx: {
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                  opacity: 1,
                },
              },
            },
          }}
        />
        <Stack direction="row" spacing={1}>
          <CustomToolbarFilterButton
            showLabel
            icon={<Iconify icon="mingcute:filter-2-fill" width={18} />}
          />
          <CustomToolbarExportButton
            showLabel
            icon={<Iconify icon="eva:cloud-download-fill" width={18} />}
            showExcel
          />
        </Stack>
      </Box>
    </Toolbar>
  );
}

/**
 * Component that displays carrier profile information on the policy details page.
 *
 * Renders:
 * - Loading skeleton while data is being fetched
 * - Carrier name and carrier ID when data is available
 * - Empty state message when carrier data is not available
 */
export const PolicyCarrierProfile: React.FC<PolicyCarrierProfileProps> = ({
  carrier,
  carrierContacts,
  isLoading = false,
}) => {
  const carrierProfileMessages = MESSAGES.POLICY.CARRIER_PROFILE;
  // Carrier Profile contacts are intentionally API-driven from PolicyViewModel.carrierContacts,
  // even while backend data remains mock-backed.
  const normalizedContacts = normalizeCarrierContactRecords(carrierContacts);
  const sortedContacts = sortCarrierContacts(normalizedContacts);
  const pamContact = getPamContact(sortedContacts);
  // PAM is represented in its dedicated card; grouped table rows must exclude PAM entries.
  const tableContacts = React.useMemo(
    () => sortedContacts.filter((contact) => !contact.isPam),
    [sortedContacts]
  );

  const renderCard = (
    title: string,
    content: React.ReactNode,
    cardId: string,
    testId: string,
    ariaLabel?: string,
    dividerAfterHeader = false
  ) => {
    const labelledBy = ariaLabel ? undefined : cardId;

    return (
      <Card
        component="section"
        aria-labelledby={labelledBy}
        aria-label={ariaLabel}
        tabIndex={0}
        sx={{ mt: 5 }}
        data-testid={testId}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
            <Typography id={cardId} variant="h6" component="h2">
              {title}
            </Typography>
          </Stack>
          {dividerAfterHeader ? <Divider sx={{ mx: -3, mb: 3 }} /> : null}
          {content}
        </CardContent>
      </Card>
    );
  };

  const renderIdentityFields = (loading: boolean) => (
    <Grid container spacing={2} columns={16}>
      <Grid size={{ xs: 16, md: 8 }}>
        <InfoBox
          label={carrierProfileMessages.NAME_LABEL}
          value={carrier?.carrierName ?? ''}
          loading={loading}
          testId="carrier-name-field"
          testIdLabel="carrier-name-label"
          testIdValue="carrier-name-value"
          sx={{ mt: 0 }}
        />
      </Grid>
      <Grid size={{ xs: 16, md: 8 }}>
        <InfoBox
          label={carrierProfileMessages.ID_LABEL}
          value={carrier?.carrierId || carrierProfileMessages.MISSING_ID}
          loading={loading}
          testId="carrier-id-field"
          testIdLabel="carrier-id-label"
          testIdValue="carrier-id-value"
          sx={{ mt: 0 }}
        />
      </Grid>
    </Grid>
  );

  // Loading state
  if (isLoading) {
    return (
      <>
        {renderCard(
          carrierProfileMessages.TITLE,
          renderIdentityFields(true),
          'carrier-profile-card-title',
          'carrier-profile-card'
        )}
        {renderCard(
          carrierProfileMessages.PAM_TITLE,
          <Skeleton data-testid="pam-skeleton" variant="rectangular" height={tableRowHeight * 2} />,
          'carrier-profile-pam-title',
          'carrier-profile-pam-card'
        )}
        {renderCard(
          carrierProfileMessages.CONTACTS_TITLE,
          <Skeleton
            data-testid="contacts-skeleton"
            variant="rectangular"
            height={tableRowHeight * 4}
          />,
          'carrier-profile-contacts-title',
          'carrier-profile-contacts-card'
        )}
      </>
    );
  }

  const renderPamContent = () => {
    if (!pamContact) {
      return (
        <Typography variant="body2" color="textSecondary">
          {carrierProfileMessages.NO_PAM_STATE}
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          columnGap: 8,
          rowGap: 4,
        }}
      >
        <Stack spacing={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {carrierProfileMessages.CONTACT_NAME_LABEL}
            </Typography>
            <Typography variant="subtitle2">{pamContact.contactName}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {carrierProfileMessages.EMAIL_LABEL}
            </Typography>
            <Typography variant="subtitle2">{pamContact.email}</Typography>
          </Box>
        </Stack>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {carrierProfileMessages.PHONE_LABEL}
          </Typography>
          <Typography variant="subtitle2">{pamContact.phone}</Typography>
        </Box>
      </Box>
    );
  };

  const renderContactsContent = () => {
    if (tableContacts.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          {carrierProfileMessages.NO_CONTACTS_STATE}
        </Typography>
      );
    }

    return (
      <Box
        data-testid="carrier-contacts-table-container"
        sx={{
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <DataGridPremium
          aria-label="Carrier contacts table"
          rows={tableContacts}
          columns={contactColumns}
          rowGroupingModel={['role']}
          groupingColDef={groupingColumnDefinition}
          initialState={{
            rowGrouping: {
              model: ['role'],
            },
            columns: {
              columnVisibilityModel: {
                role: false,
              },
            },
          }}
          slots={{
            toolbar: CarrierContactsToolbar,
          }}
          getRowId={(row) => (row.id && row.role ? `${row.id}:${row.role}` : row.id)}
          autoHeight
          hideFooter
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableAggregation
          disableVirtualization
          defaultGroupingExpansionDepth={0}
          rowHeight={tableRowHeight}
          columnHeaderHeight={tableHeaderHeight}
          showToolbar
        />
      </Box>
    );
  };

  return (
    <>
      {renderCard(
        carrierProfileMessages.TITLE,
        !carrier?.carrierName ? (
          <Typography variant="body2" color="textSecondary">
            {carrierProfileMessages.EMPTY_STATE}
          </Typography>
        ) : (
          renderIdentityFields(false)
        ),
        'carrier-profile-card-title',
        'carrier-profile-card'
      )}
      {renderCard(
        carrierProfileMessages.PAM_TITLE,
        renderPamContent(),
        'carrier-profile-pam-title',
        'carrier-profile-pam-card'
      )}
      {renderCard(
        carrierProfileMessages.CONTACTS_TITLE,
        renderContactsContent(),
        'carrier-profile-contacts-title',
        'carrier-profile-contacts-card',
        'Carrier contacts',
        true
      )}
    </>
  );
};

export default PolicyCarrierProfile;
